import { API_BASE_URL } from '../config';
import React, { useEffect, useState } from 'react';

const TYPES = ['Lab', 'Imaging', 'Procedure', 'Discharge', 'Other'];
const MAX_FILE = 1200000;

function localDateInput(date = new Date()) {
  const pad = (value) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

const emptyForm = () => ({
  TITLE: '',
  REPORT_TYPE: 'Lab',
  REPORT_DATE: localDateInput(),
  FINDINGS: '',
  APPOINTMENT_ID: '',
  FILE_NAME: '',
  MIME_TYPE: '',
  FILE_DATA: ''
});

const ClinicalReports = ({ patientId, appointmentId, visits = [] }) => {
  const resolvedPatientId = patientId && patientId._id ? patientId._id : patientId;
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const canAdd = canAddReports();

  useEffect(() => {
    if (!resolvedPatientId && !appointmentId) return undefined;
    let ignore = false;
    const load = async () => {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (resolvedPatientId) params.set('patientId', resolvedPatientId);
      if (appointmentId) params.set('appointmentId', appointmentId);
      try {
        const response = await fetch(`${API_BASE_URL}/clinical-reports?${params}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await response.json();
        if (ignore) return;
        if (!response.ok) throw new Error(data.message || 'Could not load reports.');
        setReports(Array.isArray(data) ? data : []);
      } catch (err) {
        if (!ignore) setError(err.message);
      } finally {
        if (!ignore) setLoading(false);
      }
    };
    load();
    return () => { ignore = true; };
  }, [resolvedPatientId, appointmentId]);

  const onFile = (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) {
      setForm((current) => ({ ...current, FILE_NAME: '', MIME_TYPE: '', FILE_DATA: '' }));
      return;
    }
    if (file.size > MAX_FILE) {
      setError('Report file must be under 1.2 MB.');
      event.target.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      const data = result.includes(',') ? result.split(',')[1] : result;
      setForm((current) => ({ ...current, FILE_NAME: file.name, MIME_TYPE: file.type, FILE_DATA: data }));
      setError('');
    };
    reader.readAsDataURL(file);
  };

  const save = async () => {
    if (!form.TITLE.trim()) {
      setError('Report title is required.');
      return;
    }
    setSaving(true);
    setError('');
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_BASE_URL}/clinical-reports`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          ...form,
          PATIENT_ID: resolvedPatientId,
          APPOINTMENT_ID: appointmentId || form.APPOINTMENT_ID || undefined
        })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Could not add the report.');
      setReports((current) => [data.report, ...current]);
      setForm(emptyForm());
      setShowForm(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const openFile = async (report) => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_BASE_URL}/clinical-reports/${report._id}/file`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || 'Could not open the file.');
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      setError(err.message);
    }
  };

  const remove = async (report) => {
    if (!window.confirm(`Remove ${report.TITLE}?`)) return;
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_BASE_URL}/clinical-reports/${report._id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` }
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      setError(data.message || 'Could not remove the report.');
      return;
    }
    setReports((current) => current.filter((item) => item._id !== report._id));
  };

  return (
    <section style={panelStyle} className="clinical-reports">
      <style>{'.clinical-reports label{display:block;font-size:.85rem;color:#495057;margin-bottom:.15rem}'}</style>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', alignItems: 'center' }}>
        <h4 style={sectionTitle}>Reports</h4>
        {canAdd && (
          <button type="button" onClick={() => setShowForm((open) => !open)} style={addButton}>
            {showForm ? 'Hide report form' : 'Add report'}
          </button>
        )}
      </div>
      {error && <p style={{ color: '#dc3545' }}>{error}</p>}
      {showForm && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem', marginBottom: '0.75rem' }}>
          <label>Title
            <input value={form.TITLE} onChange={(event) => setForm((current) => ({ ...current, TITLE: event.target.value }))} placeholder="CBC report" style={fieldStyle} />
          </label>
          <label>Type
            <select value={form.REPORT_TYPE} onChange={(event) => setForm((current) => ({ ...current, REPORT_TYPE: event.target.value }))} style={fieldStyle}>
              {TYPES.map((type) => <option key={type}>{type}</option>)}
            </select>
          </label>
          <label>Date
            <input type="date" value={form.REPORT_DATE} onChange={(event) => setForm((current) => ({ ...current, REPORT_DATE: event.target.value }))} style={fieldStyle} />
          </label>
          {!appointmentId && visits.length > 0 && (
            <label>Visit
              <select value={form.APPOINTMENT_ID} onChange={(event) => setForm((current) => ({ ...current, APPOINTMENT_ID: event.target.value }))} style={fieldStyle}>
                <option value="">Not tied to a visit</option>
                {visits.map((visit) => (
                  <option key={visit._id} value={visit._id}>
                    {visit.APPOINTMENT_DATE ? new Date(visit.APPOINTMENT_DATE).toLocaleDateString() : 'Visit'} {visit.APPOINTMENT_SLOT || ''}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label style={{ gridColumn: '1 / -1' }}>Findings
            <textarea value={form.FINDINGS} onChange={(event) => setForm((current) => ({ ...current, FINDINGS: event.target.value }))} rows="3" placeholder="What the report shows" style={fieldStyle} />
          </label>
          <label>File
            <input type="file" accept="application/pdf,image/jpeg,image/png,image/webp" onChange={onFile} style={fieldStyle} />
          </label>
          <div style={{ alignSelf: 'end' }}>
            <button type="button" onClick={save} disabled={saving} style={{ ...addButton, backgroundColor: '#1f4d40', color: 'white' }}>
              {saving ? 'Saving...' : 'Save report'}
            </button>
          </div>
        </div>
      )}
      {loading ? <p>Loading reports...</p> : (
        reports.length ? (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
              <thead>
                <tr>
                  {['Date', 'Type', 'Title', 'Findings', 'Added by', 'File', ''].map((header) => (
                    <th key={header || 'action'} style={cellHead}>{header}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {reports.map((report) => (
                  <tr key={report._id}>
                    <td style={cell}>{report.REPORT_DATE ? new Date(report.REPORT_DATE).toLocaleDateString() : '—'}</td>
                    <td style={cell}>{report.REPORT_TYPE}</td>
                    <td style={cell}>{report.TITLE}</td>
                    <td style={cell}>{report.FINDINGS || '—'}</td>
                    <td style={cell}>{report.ADDED_BY_NAME || '—'}</td>
                    <td style={cell}>
                      {report.HAS_FILE ? <button type="button" onClick={() => openFile(report)} style={linkButton}>{report.FILE_NAME || 'Open'}</button> : '—'}
                    </td>
                    <td style={cell}>
                      {canAdd && <button type="button" onClick={() => remove(report)} style={linkButton}>Remove</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <p style={{ margin: 0, color: '#6c757d' }}>No reports yet.</p>
      )}
    </section>
  );
};

function canAddReports() {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const names = new Set([user.role, ...(user.roles || []).map((role) => role.ROLE_NAME)].filter(Boolean));
    return names.has('Doctor') || names.has('Receptionist') || names.has('Admin');
  } catch (error) {
    return false;
  }
}

const panelStyle = { marginTop: '1rem', padding: '1rem', border: '1px solid #e9ecef', borderRadius: '8px', backgroundColor: '#fff' };
const sectionTitle = { margin: 0, color: '#1f4d40' };
const fieldStyle = { width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc', boxSizing: 'border-box' };
const addButton = { padding: '8px 12px', backgroundColor: 'white', color: '#0f766e', border: '1px solid #0f766e', borderRadius: '4px', cursor: 'pointer' };
const linkButton = { padding: 0, border: 'none', background: 'none', color: '#0f766e', cursor: 'pointer', textDecoration: 'underline' };
const cellHead = { padding: '8px', borderBottom: '1px solid #dee2e6', textAlign: 'left', backgroundColor: '#f8f9fa' };
const cell = { padding: '8px', borderBottom: '1px solid #f1f3f5', verticalAlign: 'top' };

export default ClinicalReports;
